import React, { useContext } from 'react';
import { useQuery } from 'react-query';

import ApiContext from 'api/ApiContext';
import MunicipalityList from 'lib/MunicipalityList/MunicipalityList';
import { CountingCirclesRecord, PollDetails } from 'api/api.types';
import { PartyBarData } from 'lib/PartyBar/partybar.types';
import PartyBarList from 'lib/PartyBarList/PartyBarList';

const renderAccordeon = (
  listsWithDetails: PartyBarData[],
  currentElectionYear: number,
  previousElectionResults: PollDetails,
  prevYear?: number,
) => {
  return ({ lists }: CountingCirclesRecord, prevRecord: CountingCirclesRecord) => {
    const idsOfUsedPrevLists: number[] = [];
    const partyBarData: PartyBarData[] = lists.map((list) => {
      const listWithDetails = listsWithDetails.find(
        (li) => li.listId === list.list_group_id,
      );

      // find list from previous election via partyId since only this is consistent
      const partyIdOfCurrentList = listWithDetails?.partyId;
      const listIdOfPrevList = previousElectionResults?.list_groups?.find(
        (listGroup) => listGroup.party_id === partyIdOfCurrentList,
      )?.id;

      const prevList = prevRecord?.lists?.find(
        (li) => li.list_group_id === listIdOfPrevList,
      );

      if (prevList) {
        idsOfUsedPrevLists.push(prevList.list_group_id);
      }

      return {
        title: listWithDetails?.title,
        shortTitle: listWithDetails?.shortTitle,
        seatsClaimed: list?.results?.seat_claim,
        seatsClaimedPrev: prevList?.results?.seat_claim,
        listId: listWithDetails?.listId,
        listNumber: listWithDetails?.listNumber,
        competedInPrevElection: listWithDetails?.competedInPrevElection,
      };
    });

    // add lists that won a seat in previous election but not in current one
    const additionalListsFromPrev = prevRecord?.lists
      .filter((list) => {
        return (
          !idsOfUsedPrevLists.includes(list.list_group_id) && list.results?.seat_claim > 0
        );
      })
      .map((list) => {
        return {
          title: list.title.de,
          shortTitle: list.short_title.de,
          seatsClaimed: null,
          seatsClaimedPrev: list.results.seat_claim,
          listId: list.list_group_id,
          listNumber: 'z', // these lists should be sorted last
          competedInCurrentElection: false,
          competedInPrevElection: true,
        };
      });

    if (additionalListsFromPrev) {
      partyBarData.push(...additionalListsFromPrev);
    }

    const numOfSeatsCurrentElection = partyBarData.reduce(
      (acc, list) => acc + (list.seatsClaimed || 0),
      0,
    );

    const numOfSeatsPrevElection = partyBarData.reduce(
      (acc, list) => acc + (list.seatsClaimedPrev || 0),
      0,
    );

    return (
      <PartyBarList
        isFinal
        lists={partyBarData}
        type="seats"
        currentElectionYear={currentElectionYear}
        prevElectionYear={prevYear}
        numOfSeatsCurrentElection={numOfSeatsCurrentElection}
        numOfSeatsPrevElection={numOfSeatsPrevElection}
      />
    );
  };
};

interface Props {
  lists: PartyBarData[];
  pollId: string;
  currentElectionYear: number;
  previousElectionId?: string;
  previousElectionResults: PollDetails;
  prevYear: number;
}

const ProportionalSeatsDistricts = ({
  lists,
  pollId,
  currentElectionYear,
  previousElectionId,
  previousElectionResults,
  prevYear,
}: Props) => {
  const { getAggregatingDomains } = useContext(ApiContext);
  const query = useQuery(['aggregating_domains', pollId], () =>
    getAggregatingDomains(pollId),
  );
  const queryPrevElection = useQuery(['aggregating_domains', previousElectionId], () =>
    getAggregatingDomains(previousElectionId),
  );

  return (
    <MunicipalityList
      hideSearchbar
      showNumberOfSeats
      checksum={query.data?.meta?.data_checksum_sha256}
      districts={query.data?.data?.records}
      prevDistricts={queryPrevElection.data?.data?.records}
      render={renderAccordeon(
        lists,
        currentElectionYear,
        previousElectionResults,
        prevYear,
      )}
    />
  );
};

export default ProportionalSeatsDistricts;
